import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useTabLabels } from "./settings-layout";
import styles from "./settings.module.css";

/**
 * The parts every Settings pane shares: a head, and a branch-scoped read.
 *
 * Four of the nine panes are scoped to one location and load one resource
 * apiece, and before this they were five `useEffect`s and five reload callbacks
 * in a single 454-line `branch-page.tsx`. Splitting that file without a shared
 * piece here would have copied the same twenty lines four times.
 */

/**
 * A pane's title, its one-line explanation, and — when it is scoped — the
 * location it is showing.
 *
 * **The scope chip repeats the switcher's answer where the reader is looking.**
 * The rail is 232px away and off to the left; a pane that silently shows one
 * location's prices while the eye is on another's name in the rail is the
 * mistake this costs one chip to prevent.
 */
export function PaneHead({
  title,
  scope,
  testId,
}: {
  title: string;
  scope?: string | undefined;
  /**
   * For a pane whose title is *data* rather than a fixed label, so a test can
   * assert what it says. The location page needs it — `branch-title` is how
   * four specs check they opened the location they meant to.
   */
  testId?: string | undefined;
}): React.JSX.Element {
  const repeatsTab = useTabLabels().has(title);
  return (
    <header className={styles.paneHead}>
      <h2
        className={repeatsTab ? `${styles.paneTitle} ${styles.paneTitleTabbed}` : styles.paneTitle}
        data-testid={testId}
      >
        {title}
      </h2>
      {scope !== undefined && (
        <span className={styles.paneScope} data-testid="pane-scope">
          {scope}
        </span>
      )}
    </header>
  );
}

/**
 * Read one branch-scoped resource, once there is a branch to read it for.
 *
 * `enabled` is the whole subtlety. A branch-scoped request carries the active
 * branch as a header (`client.ts`), and the guard refuses one that is absent —
 * so firing on mount, before the switcher has settled on a location, produced
 * an error toast on every arrival. Null is "not yet", never "none"; the same
 * distinction `useActiveBranch` documents.
 *
 * `load` must be stable — wrap it in `useCallback` at the call site, or this
 * re-reads on every render.
 */
export function useScopedResource<T>(
  load: (signal?: AbortSignal) => Promise<T>,
  whenFailed: string,
  fail: (message: string) => void,
  enabled: boolean,
  /**
   * **The active branch id, and leaving it out is a bug that looks like
   * nothing.**
   *
   * A branch-scoped request does not take the branch as an argument — it
   * travels as a header that `client.ts` reads from one global
   * (`activeBranch`). So `listChairs` is byte-identical before and after
   * somebody switches location, its `useCallback` is stable, and this effect
   * has nothing to notice.
   *
   * What that produced, and what this parameter exists to stop: switching
   * location in the rail relabelled the pane and left the previous location's
   * chairs on screen underneath the new name. Caught in the browser, not by a
   * type — the dependency is real and invisible, so it has to be passed in by
   * hand.
   */
  scopeKey: string | null,
): { data: T | null; reload: () => Promise<void> } {
  const [data, setData] = useState<T | null>(null);

  useEffect(() => {
    if (!enabled || scopeKey === null) {
      // Back to "not yet" rather than keeping the last location's rows on
      // screen under the new location's name, which is what made switching
      // branch look as though nothing had happened.
      setData(null);
      return;
    }

    const controller = new AbortController();

    load(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setData(rows);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setData(null);
        fail(describeRead(cause, whenFailed));
      });

    return () => controller.abort();
  }, [load, enabled, fail, whenFailed, scopeKey]);

  return {
    data,
    reload: async () => {
      setData(await load());
    },
  };
}

function describeRead(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message !== "" ? cause.message : fallback;
}

/**
 * What a scoped pane shows when there is no location to scope it to.
 *
 * Two different situations, and telling them apart is the point: a clinic with
 * no locations at all needs the link that creates one, while a location this
 * admin is not rostered to needs explaining rather than fixing.
 *
 * **Not rostered is decision S7**: `BranchScopeGuard` admits on
 * `branch_memberships` rather than on role, so an admin who is not a member of
 * a branch genuinely cannot read its chairs. The screen says so instead of
 * offering a control the server would refuse.
 */
export function NoScope({
  what,
  reason,
}: {
  /** What the pane would have shown — "chairs", "procedures". */
  what: string;
  reason: "none" | "unchosen" | "not-a-member";
}): React.JSX.Element {
  if (reason === "none") {
    return (
      <p className={styles.blank} data-testid="scope-no-locations">
        This clinic has no locations yet, and {what} belong to one.{" "}
        <Link to="/settings/locations">Open a location</Link> and its {what} appear here.
      </p>
    );
  }

  if (reason === "not-a-member") {
    return (
      <p className={styles.blank} data-testid="not-rostered">
        You are not rostered at this location, so its {what} are not yours to read. An administrator
        who works here can add you to it, or you can pick a different location above.
      </p>
    );
  }

  return (
    <p className={styles.blank} data-testid="scope-unchosen">
      Pick a location in the rail, and its {what} appear here.
    </p>
  );
}

/** Which of `NoScope`'s three situations applies, or null when there is a scope. */
export function scopeProblem(
  branches: readonly { id: string }[] | null,
  branch: { isMember: boolean } | null,
): "none" | "unchosen" | "not-a-member" | null {
  if (branches !== null && branches.length === 0) return "none";
  if (branch === null) return "unchosen";
  if (!branch.isMember) return "not-a-member";
  return null;
}
